/**
 * Prize-pool maths shared by the live server and by offline play (which shows a provisional result on the
 * device and is re-computed by the server when the match is claimed).
 */
import type { Standing } from './core/types.js';

export const DEFAULT_PAYOUTS: readonly number[] = [60, 30, 10, 0];

/** Splits the net pot by finishing place; tied players share the combined percentages of their positions. */
export function computeMatchPayouts(
  pot: number,
  standings: readonly Standing[],
  payoutPercents: readonly number[],
  rakePercent: number,
): { rake: number; payouts: Map<number, number> } {
  const rake = Math.floor((pot * rakePercent) / 100);
  const net = pot - rake;
  const sorted = [...standings].sort((a, b) => a.place - b.place);
  const payouts = new Map<number, number>();
  let position = 0;
  let distributed = 0;
  const groups = new Map<number, Standing[]>();
  for (const s of sorted) groups.set(s.place, [...(groups.get(s.place) ?? []), s]);
  for (const [, group] of [...groups.entries()].sort((a, b) => a[0] - b[0])) {
    let pct = 0;
    for (let i = 0; i < group.length; i++) pct += payoutPercents[position + i] ?? 0;
    position += group.length;
    const each = Math.floor((net * pct) / 100 / group.length);
    for (const s of group) {
      payouts.set(s.seat, each);
      distributed += each;
    }
  }
  // Rounding remainder goes to the winner(s) so the pot is always fully distributed.
  const totalPct = payoutPercents.reduce((a, b) => a + b, 0);
  const shouldDistribute = Math.floor((net * Math.min(100, totalPct)) / 100);
  const first = sorted[0];
  if (first && shouldDistribute > distributed) payouts.set(first.seat, (payouts.get(first.seat) ?? 0) + (shouldDistribute - distributed));
  return { rake: rake + (net - shouldDistribute), payouts };
}

/** What a table's rules JSON says about prizes. */
export function tablePrizeRules(rules: Record<string, unknown>): { payoutPercents: number[]; rakePercent: number } {
  const p = rules.payoutPercents;
  return {
    payoutPercents: Array.isArray(p) && p.every((x) => typeof x === 'number') ? (p as number[]) : [...DEFAULT_PAYOUTS],
    rakePercent: typeof rules.rakePercent === 'number' ? rules.rakePercent : 0,
  };
}
